import React from 'react';

const AMLForm = ({ formData, handleChange }) => {
  return (
    <section className="border-b border-gray-100 pb-8 md:pb-12">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-gray-900 tracking-tight">
          <span className="block text-sm font-normal text-gray-500 mt-1">Step 3 of 3: AML Declaration</span>
        </h2>
        
        <div className="space-y-6 md:space-y-8">
          {/* Progress indicator */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-start">
            <div className="bg-gray-200 text-gray-600 p-2 rounded-lg mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Anti-Money Laundering Declaration</h3>
              <p className="text-sm text-gray-600 mt-1">Please complete this AML compliance form as required by financial regulations.</p>
            </div>
          </div>

          {/* Main Form */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
            {/* Client Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                placeholder="Enter your full legal name"
                required
              />
            </div>

            {/* PEP Question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Are you or any UBO a Politically Exposed Person (PEP)? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isPEP"
                    value="Yes"
                    checked={formData.isPEP === 'Yes'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                    required
                  />
                  <span className="ml-2 text-gray-700">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isPEP"
                    value="No"
                    checked={formData.isPEP === 'No'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                  />
                  <span className="ml-2 text-gray-700">No</span>
                </label>
              </div>
            </div>

            {/* High-risk industries */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Is your source of funds associated with high-risk industries? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="highRiskIndustries"
                    value="Yes"
                    checked={formData.highRiskIndustries === 'Yes'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                    required
                  />
                  <span className="ml-2 text-gray-700">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="highRiskIndustries"
                    value="No"
                    checked={formData.highRiskIndustries === 'No'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                  />
                  <span className="ml-2 text-gray-700">No</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">(e.g., gambling, arms, adult entertainment, etc.)</p>
            </div>

            {/* Offshore jurisdictions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Is your source of funds associated with offshore jurisdictions? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="offshoreJurisdictions"
                    value="Yes"
                    checked={formData.offshoreJurisdictions === 'Yes'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                    required
                  />
                  <span className="ml-2 text-gray-700">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="offshoreJurisdictions"
                    value="No"
                    checked={formData.offshoreJurisdictions === 'No'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                  />
                  <span className="ml-2 text-gray-700">No</span>
                </label>
              </div>
            </div>

            {/* Investigated for crimes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Have you ever been investigated for money laundering or financial crimes? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="investigatedForCrimes"
                    value="Yes"
                    checked={formData.investigatedForCrimes === 'Yes'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                    required
                  />
                  <span className="ml-2 text-gray-700">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="investigatedForCrimes"
                    value="No"
                    checked={formData.investigatedForCrimes === 'No'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                  />
                  <span className="ml-2 text-gray-700">No</span>
                </label>
              </div>
            </div>

            {/* Explanation */}
            {(formData.isPEP === 'Yes' || 
              formData.highRiskIndustries === 'Yes' || 
              formData.offshoreJurisdictions === 'Yes' || 
              formData.investigatedForCrimes === 'Yes') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Please provide explanation <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="explanation"
                  value={formData.explanation}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                  rows="4"
                  placeholder="Provide details about your situation"
                  required
                ></textarea>
              </div>
            )}

            {/* Signature Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Signature <span className="text-red-500">*</span>
              </label>
              <div className="bg-gray-50/50 p-4 rounded-lg border border-gray-100 mb-4">
                <div className="flex">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Instructions:</span> Sign a blank white paper with black ink, then upload a clear photo or scan.<br />
                    Your signature must match the one on your government ID and be fully visible.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col w-full max-w-lg border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 px-4">
                    <svg className="w-8 h-8 mb-4 text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      {formData.amlSignatureFile ? formData.amlSignatureFile.name : 'PNG, JPG, PDF (MAX. 5MB)'}
                    </p>
                  </div>
                  <input 
                    type="file" 
                    name="amlSignatureFile" 
                    onChange={handleChange} 
                    className="hidden" 
                    required 
                  />
                </label>
              </div>
            </div>

            {/* Date */}
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="amlDate"
                value={formData.amlDate}
                onChange={handleChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                required
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AMLForm;